import axios, { AxiosResponse } from "axios";

const axiosApi = axios.create({
  baseURL: __webpack_public_path__ + 'api',
  // baseURL: `http://localhost:8111/api`,
  // baseURL: `https://demo.brdstk.net/bam/api`,
  timeout: 12000,
  headers: {
    "Content-Type": "application/json",
    "X-TzOffset": new Date().getTimezoneOffset() * -1,
  },
});

const MAX_REQUESTS_COUNT = 5;
const INTERVAL_MS = 10;
let PENDING_REQUESTS = 0;

const axiosBatchAPI = axiosApi;
axiosBatchAPI.interceptors.request.use(function (config) {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
        PENDING_REQUESTS++;
        clearInterval(interval);
        resolve(config);
      }
    }, INTERVAL_MS);
  });
});

axiosBatchAPI.interceptors.response.use(
  function (response) {
    PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
    return Promise.resolve(response);
  },
  function (error) {
    PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
    return Promise.reject(error);
  }
);

interface UserAuthorityList {
  [index: number]: string;
}

interface LoginResp {
  UserId: string;
  Token: string;
  DisplayName: string;
  Authority: UserAuthorityList;
}

export interface ResourceList {
  // items?: []
  count: number;
}

export interface AuthorityList extends ResourceList {
  items?: UserAuthorityList;
}

export interface EmployeeList extends ResourceList {
  items?: Employee[];
}
export interface Employee {
  id: string;
  EmployeeUId: string;
  CompanyUId: string;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  ReferenceId?: string;
  Departments?: string[];
  DepartmentRefs?: string[];
  Tags?: string[];
  Suppressed: boolean;
}

export interface CompanyList extends ResourceList {
  items?: Company[];
}

export interface SheqsyCompany {
  id: string;
  CompanyUId: string;
  Name: string;
  Departments?: string[];
  ReceiverCode?: string;
  SimsClass?: string;
  LastUpdate: string;
  LastExport: string;
}


export interface SheqsyCompanyList extends ResourceList {
  items?: SheqsyCompany[];
}

export interface SheqsyCompanyDepartment {
  id: string;
  DepartmentUId: string
  CompanyUId: string
  Name: string;
  Escalations: SheqsyCompanyDepartmentEscalation[]
  LastUpdate: string;
}

export interface SheqsyCompanyDepartmentEscalation {
  Sequence: number;
  Type: string;
  Name?: string;
  Email?: string;
  Phone?: string;
}


export interface SheqsyCompanyDepartmentList extends ResourceList {
  items?: SheqsyCompanyDepartment[];
}

export interface Company {
  id: number;
  CompanyUId: string;
  Name: string;
  DisplayName?: string;
  Email?: string;
  Phone?: string;
  Status?: string;
  Producers?: CompanyProducer[];
}

export interface CompanyProducer {
  Name: string;
  Token: string;
  Enabled: boolean;
  TestEnabled: boolean;
  TestUser: string;
  TestPassword: string;
}

export interface GeneratedToken {
  Token: string;
}

export interface CorrelationList extends ResourceList {
  items?: Correlation[];
}

export interface Correlation {
  id: string;
  SourceEvent: Record<string, unknown>;
  ReceiverAction: ReceiverAction[];
  LatestReceiverStatus: string;
}

export interface ReceiverAction {
  id: string;
  Generated: string;
  ReceiverStatus: string;
  Result: boolean;
  TemplateId?: string;
}

export interface TemplateList extends ResourceList {
  items?: Template[];
}

export interface Template {
  id: string;
  Consumer: string;
  DataSource: string;
  SignalType: TemplateSignalType;
  Envelope: TemplateEnvelope;
  Body: Record<string, unknown>;
  Enabled: boolean;
}

export interface TemplateSignalType {
  id?: string;
  Message?: string;
  Supported?: boolean;
}

export interface TemplateEnvelope {
  DataType: string;
  Wrapper: string;
}

export interface UserList extends ResourceList {
  items?: User[];
}

export interface User {
  id: string;
  Company: Company;
  Email: string;
  Username: string;
  Name: string;
  DisplayName: string;
  Password: string | undefined;
  NewPassword: string | undefined;
  Phone: string;
  Status: string;
  Authority: string[];
  Session: UserSession;
}

export interface UserSession {
  Token: string;
  Lifetime: number;
  Created: string;
  Bumped: string;
}

export interface SecurityEventList extends ResourceList {
  items?: SecurityEvent[];
}

export interface SheqsyMessage {
  Message: string;
  Supported: boolean;
}
export interface SheqsyMessageList extends ResourceList {
  items?: SheqsyMessage[];
}

export interface SecurityEvent {
  id: string;
  ActivityUId: string;
  Timestamp: string;
  StatusCode: number;
  Latitude?: number;
  Longitude?: number;
  CompanyUId: string;
  Message: string;
  EmployeeUId: string;
  Employee: Employee;
  Activity: SecurityEventActivity;
  ExployeeExternalId?: number;
  FixDateTimeUTC?: number;
  ActivityExternalId?: number;
  MismatchData?: EventEmployeeMismatchData[];
}

export interface EventEmployeeMismatchData {
  Field: string;
  OldValue: string;
  NewValue: string;
}

export interface SecurityEventActivity {
  ActivityType: string;
  Address?: string;
  Name: string;
  Forms?: string[];
  StartLocation?: SecurityEventActivityLocation;
  PanicLocation?: SecurityEventActivityLocation;
}

export interface SecurityEventActivityLocation {
  Address?: string;
  Latitude?: number;
  Longitude?: number;
}

export interface SimsSignalList extends ResourceList {
  items?: SimsSignal[];
}

export interface SimsSignal {
  CorrelationEvent: Correlation;
  Generated: string;
  ConverstionStatus: string;
  State: string;
  Attempts: number;
  Body: string;
}

export interface ReceiverExportList extends ResourceList {
  items?: ReceiverExport[]
}

export interface ReceiverExport {
  id: string;
  Name: string;
  Destination: string;
  Type: string;
  Generated: string;
  Processed: string;
  Exported: string;
  SheqsyCompanyUId: string;
  EmployeeList: string[];
  Files: ReceiverExportFile[]
}

export interface ReceiverExportFile {
  FileId: string;
  Name: string
}

export interface ReceiverEventList extends ResourceList {
  items?: ReceiverEvent[];
}

export interface ReceiverEvent {
  Company?: Company;
  Initiator: string;
  Class: string;
  Condition: string;
  Generated: string;
  Message?: string;
  ClosingEvent?: ReceiverEvent;
  Data?: string;
}

export interface ReceiverStatsSummary {
  last: ReceiverEvent | SimsSignal | SecurityEvent;
  counts: {
    tenmin: number;
    hour: number;
    today: number;
    total: number;
  };
}
export interface ReceiverStats {
  actions: ReceiverStatsSummary;
  alerts: ReceiverStatsSummary;
  sheqsy: ReceiverStatsSummary;
  signals: ReceiverStatsSummary;
}

export interface SheqsyArchiveEventCorrelation {
  ReceiverStatus: string;
  ReceiverAction: ReceiverAction[];
  Signal: SimsSignal;
}

export interface SheqsyArchiveEvent {
  CorrelationEvents: SheqsyArchiveEventCorrelation[];
  Timestamp: string;
  StatusCode: number;
  Latitude: number;
  Longitude: number;
  CompanyUId: string;
  Message: string;
  EmployeeUId: string;
  Activity: SecurityEvent[];
}

export interface SheqsyArchive {
  ArchiveDate: string;
  ActivityUId: string;
  EmployeeUId: string;
  SheqsyEvents: SheqsyArchiveEvent[];
}

export interface SheqsyArchiveList extends ResourceList {
  items?: SheqsyArchive[];
}

export interface ReceiverArchiveEvent {
  Date: string;
  Message: string;
  Data: string;
}

export interface ReceiverArchive {
  ArchiveDate: string;
  EventDate: string;
  EventType: string;
  Events: ReceiverArchiveEvent[];
}

export interface ReceiverArchiveList extends ResourceList {
  items?: ReceiverArchive[];
}

// General Request Parameter Handling
export interface RequestParams {
  Filters?: RequestFilter[];
  Ranges?: RequestRange;
  Paging?: RequestPaging;
  Sorting?: RequestSort;
}

export interface PageBounding {
  number: number;
  current: boolean;
}

export interface RequestPaging {
  Skip: number;
  Limit: number;
  Options: number[];
}

export interface RequestRange {
  Field: string;
  From?: string;
  To?: string;
}

export interface RequestSort {
  Pattern: string;
}

export interface RequestFilter {
  Filter: string;
  Expr?: any;
  Match?: string;
  Eq?: string;
  Ne?: string;
  In?: string[];
  Nin?: string[];
}

const API_AUTH = "/auth";
const API_TOKEN = "/token";
const API_SHEQSY_EMPLOYEE = "/sheqsy/employee";
const API_SHEQSY_COMPANY = "/sheqsy/company";
const API_SHEQSY_DEPARTMENT = "/sheqsy/department";
const API_SHEQSY_SECEVENT = "/sheqsy/security_event";
const API_SHEQSY_MESSAGE = "/sheqsy/message";
const API_CORRELATION = "/correlation";
const API_EXPORTER = "/receiver/export";
const API_SIMS_SIGNAL = "/sims/signal";
const API_TEMPLATE = "/template";
const API_COMPANY = "/company";
const API_AUTHZ = "/userauthz";
const API_USER = "/user";
const API_EVENT = "/event";
const API_ALERT = "/alert";
const API_STATS = "/stats";
const API_ARCH_SHEQSY = "/archives/sheqsy";
const API_ARCH_RCVR = "/archives/receiver";

export default {
  doLogin(user: string, password: string): Promise<AxiosResponse<LoginResp>> {
    return axiosApi.post(`${API_AUTH}`, {
      username: user,
      password: password,
    });
  },
  doLogout(): void {
    axiosApi.delete(`${API_AUTH}`);
  },
  setAuthToken(token: string): void {
    if (token != "") {
      axiosApi.defaults.headers.common["Authorization"] = "Bearer " + token;
      return;
    }
    delete axiosApi.defaults.headers.common["Authorization"];
  },
  checkAuthToken(): Promise<AxiosResponse<string>> {
    return axiosApi.head(`${API_AUTH}`);
  },
  generateProducerToken(): Promise<AxiosResponse<GeneratedToken>> {
    return axiosApi.post(`${API_TOKEN}/producer`);
  },
  getSheqsyArchiveList(
    request_params: RequestParams
  ): Promise<AxiosResponse<SheqsyArchiveList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_ARCH_SHEQSY}${query_params}`);
  },
  getSheqsyArchive(id: string): Promise<AxiosResponse<SheqsyArchive>> {
    return axiosApi.get(`${API_ARCH_SHEQSY}/${id}`);
  },
  deleteSheqsyArchive(id: string): Promise<AxiosResponse<SheqsyArchive>> {
    return axiosApi.delete(`${API_ARCH_SHEQSY}/${id}`);
  },
  getReceiverArchiveList(
    request_params: RequestParams
  ): Promise<AxiosResponse<ReceiverArchiveList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_ARCH_RCVR}${query_params}`);
  },
  getReceiverArchive(id: string): Promise<AxiosResponse<ReceiverArchive>> {
    return axiosApi.get(`${API_ARCH_RCVR}/${id}`);
  },
  deleteReceiverArchive(id: string): Promise<AxiosResponse<AxiosResponse>> {
    return axiosApi.delete(`${API_ARCH_RCVR}/${id}`);
  },
  getEmployeeList(
    request_params: RequestParams
  ): Promise<AxiosResponse<EmployeeList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SHEQSY_EMPLOYEE}${query_params}`);
  },
  getEmployee(id: string): Promise<AxiosResponse<Employee>> {
    return axiosApi.get(`${API_SHEQSY_EMPLOYEE}/${id}`);
  },
  saveEmployee(employee: Employee): Promise<AxiosResponse<Employee>> {
    return axiosApi.patch(
      `${API_SHEQSY_EMPLOYEE}/${employee.EmployeeUId}`,
      employee
    );
  },
  createEmployee(employee: Employee): Promise<AxiosResponse<Employee>> {
    return axiosApi.post(`${API_SHEQSY_EMPLOYEE}`, employee);
  },
  batchGetEmployee(id: string): Promise<AxiosResponse<Employee>> {
    return axiosBatchAPI.get(`${API_SHEQSY_EMPLOYEE}/${id}`);
  },
  batchSaveEmployee(employee: Employee): Promise<AxiosResponse<Employee>> {
    return axiosBatchAPI.patch(
      `${API_SHEQSY_EMPLOYEE}/${employee.EmployeeUId}`,
      employee
    );
  },
  batchCreateEmployee(employee: Employee): Promise<AxiosResponse<Employee>> {
    return axiosBatchAPI.post(`${API_SHEQSY_EMPLOYEE}`, employee);
  },
  deleteEmployee(id: string): Promise<AxiosResponse> {
    return axiosApi.delete(`${API_SHEQSY_EMPLOYEE}/${id}`);
  },
  getSheqsyCompanyList(request_params: RequestParams
  ): Promise<AxiosResponse<SheqsyCompanyList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SHEQSY_COMPANY}${query_params}`);
  },
  getSheqsyCompany(id: string): Promise<AxiosResponse<SheqsyCompany>> {
    return axiosApi.get(`${API_SHEQSY_COMPANY}/${id}`)
  },
  saveSheqsyCompany(company: SheqsyCompany): Promise<AxiosResponse<SheqsyCompany>> {
    return axiosApi.patch(`${API_SHEQSY_COMPANY}/${company.CompanyUId}`, company);
  },
  getSheqsyDepartmentList(request_params: RequestParams): Promise<AxiosResponse<SheqsyCompanyDepartmentList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SHEQSY_DEPARTMENT}${query_params}`)
  },
  getSecurityEventList(
    request_params: RequestParams
  ): Promise<AxiosResponse<SecurityEventList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SHEQSY_SECEVENT}${query_params}`);
  },
  getSecurityEvent(id: string): Promise<AxiosResponse<SecurityEvent>> {
    return axiosApi.get(`${API_SHEQSY_SECEVENT}/${id}`);
  },
  getSheqsyMessageList(
    request_params: RequestParams
  ): Promise<AxiosResponse<SheqsyMessageList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SHEQSY_MESSAGE}${query_params}`);
  },
  getSheqsyMessage(id: string): Promise<AxiosResponse<SheqsyMessage>> {
    return axiosApi.get(`${API_SHEQSY_MESSAGE}/${id}`);
  },
  getCorrelationList(
    request_params: RequestParams
  ): Promise<AxiosResponse<CorrelationList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_CORRELATION}${query_params}`);
  },
  saveCorrelation(
    correlation: Correlation
  ): Promise<AxiosResponse<Correlation>> {
    return axiosApi.patch(`${API_CORRELATION}/${correlation.id}`, correlation);
  },
  getCorrelation(id: string): Promise<AxiosResponse<Correlation>> {
    return axiosApi.get(`${API_CORRELATION}/${id}`);
  },
  getSimsSignalList(
    request_params: RequestParams
  ): Promise<AxiosResponse<SimsSignalList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_SIMS_SIGNAL}${query_params}`);
  },
  getSimsSignal(id: string): Promise<AxiosResponse<SimsSignal>> {
    return axiosApi.get(`${API_SIMS_SIGNAL}/${id}`);
  },
  getReceiverExportList(request_params: RequestParams): Promise<AxiosResponse<ReceiverExportList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_EXPORTER}${query_params}`)
  },
  getReceiverExport(id: string): Promise<AxiosResponse<ReceiverExport>> {
    return axiosApi.get(`${API_EXPORTER}/${id}`)
  },
  saveReceiverExport(receiver_export: ReceiverExport): Promise<AxiosResponse<ReceiverExport>> {
    return axiosApi.patch(`${API_EXPORTER}/${receiver_export.id}`, receiver_export);
  },
  getReceiverExportFile(export_id: string, file_id: string): Promise<AxiosResponse> {
    return axiosApi.get(`${API_EXPORTER}/${export_id}/${file_id}`)
  },
  createReceiverExport(receiver_export: ReceiverExport): Promise<AxiosResponse<ReceiverExport>> {
    return axiosApi.post(`${API_EXPORTER}`, receiver_export)
  },
  deleteReceiverExport(receiver_export: ReceiverExport): Promise<AxiosResponse> {
    return axiosApi.delete(`${API_EXPORTER}/${receiver_export.id}`);
  },
  getReceiverTemplateList(
    request_params: RequestParams
  ): Promise<AxiosResponse<TemplateList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_TEMPLATE}${query_params}`);
  },
  getReceiverTemplate(id: string): Promise<AxiosResponse<Template>> {
    return axiosApi.get(`${API_TEMPLATE}/${id}`);
  },
  saveReceiverTemplate(template: Template): Promise<AxiosResponse<Template>> {
    return axiosApi.patch(`${API_TEMPLATE}/${template.id}`, template);
  },
  createReceiverTemplate(type: string): Promise<AxiosResponse<Template>> {
    return axiosApi.post(`${API_TEMPLATE}/${type}`);
  },
  deleteReceiverTemplate(id: string): Promise<AxiosResponse> {
    return axiosApi.delete(`${API_TEMPLATE}/${id}`);
  },
  getReceiverCompanyList(
    request_params: RequestParams
  ): Promise<AxiosResponse<CompanyList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_COMPANY}${query_params}`);
  },
  getReceiverCompany(id: string): Promise<AxiosResponse<Company>> {
    return axiosApi.get(`${API_COMPANY}/${id}`);
  },
  saveReceiverCompany(company: Company): Promise<AxiosResponse<Company>> {
    return axiosApi.patch(`${API_COMPANY}/${company.CompanyUId}`, company);
  },
  getUserList(request_params: RequestParams): Promise<AxiosResponse<UserList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_USER}${query_params}`);
  },
  getUser(id: string): Promise<AxiosResponse<User>> {
    return axiosApi.get(`${API_USER}/${id}`);
  },
  saveUser(user: User): Promise<AxiosResponse<User>> {
    return axiosApi.patch(`${API_USER}/${user.id}`, user);
  },
  createUser(user: User): Promise<AxiosResponse<User>> {
    return axiosApi.post(`${API_USER}`, user);
  },
  deleteUser(id: string): Promise<AxiosResponse> {
    return axiosApi.delete(`${API_USER}/${id}`);
  },
  getAuthorityList(): Promise<AxiosResponse<AuthorityList>> {
    return axiosApi.get(`${API_AUTHZ}`);
  },
  getEventList(
    request_params: RequestParams
  ): Promise<AxiosResponse<ReceiverEventList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_EVENT}${query_params}`);
  },
  getAlerts(
    request_params: RequestParams
  ): Promise<AxiosResponse<ReceiverEventList>> {
    const query_params = this.buildParams(request_params);
    return axiosApi.get(`${API_ALERT}${query_params}`);
  },
  getOpenAlerts(): Promise<AxiosResponse<ReceiverEventList>> {
    return axiosApi.get(`${API_ALERT}/open`);
  },
  getSystemStats(): Promise<AxiosResponse<ReceiverStats>> {
    return axiosApi.get(`${API_STATS}`);
  },
  createRequestParams(
    request_filter: Record<string, any>,
    request_paging: RequestPaging,
    request_sorting?: RequestSort
  ): RequestParams {
    const request_params = {} as RequestParams;
    for (const [key, value] of Object.entries(request_filter)) {
      if (value == undefined || key == undefined || value === "") {
        continue;
      }

      if (!request_params.Filters) {
        request_params.Filters = [];
      }

      if (key == "$expr") {
        request_params.Filters.push({ Filter: key, Expr: value });
      }

      if (["string", "number"].includes(typeof value)) {
        const str_value = value as string;
        request_params.Filters.push({ Filter: key, Match: str_value });
        continue;
      }

      const is_banged = key.substr(0, 1) === "!";
      if (["boolean"].includes(typeof value)) {
        const str_value = value as string;
        if (is_banged) {
          request_params.Filters.push({ Filter: key, Ne: str_value });
        }
        request_params.Filters.push({ Filter: key, Eq: str_value });
        continue;
      }

      if (Array.isArray(value)) {
        if (is_banged) {
          request_params.Filters.push({ Filter: key, Nin: value });
          continue;
        }
        request_params.Filters.push({ Filter: key, In: value });
        continue;
      }

      if (["object"].includes(typeof value)) {
        request_params.Filters.push({ Filter: key, ...value })
        continue
      }
    }
    request_params.Paging = request_paging;
    if (request_sorting) {
      request_params.Sorting = request_sorting;
    }
    return request_params;
  },
  buildParams(request_params: RequestParams): string {
    const query_params = [];
    if (request_params.Filters) {
      query_params.push(`filters=${JSON.stringify(request_params.Filters)}`);
    }
    if (request_params.Ranges) {
      query_params.push(`range=${request_params.Ranges.Field}`);
      if (request_params.Ranges.From) {
        query_params.push(`from=${request_params.Ranges.From}`);
      }
      if (request_params.Ranges.To) {
        query_params.push(`to=${request_params.Ranges.To}`);
      }
    }
    if (request_params.Sorting) {
      query_params.push(`sort=${request_params.Sorting.Pattern}`);
    }
    if (request_params.Paging) {
      if (request_params.Paging.Limit) {
        query_params.push(`limit=${request_params.Paging.Limit}`);
      }
      if (request_params.Paging.Skip) {
        query_params.push(`skip=${request_params.Paging.Skip}`);
      }
    }
    if (query_params.length) {
      return "?" + query_params.join("&");
    }
    return "";
  },
};
